import * as signalR from "@microsoft/signalr";
import { BASE_API_URL } from "constants/config";
import { toastEmitter } from "utils/toast.util";

class SignalRService {
  private static instance: SignalRService;
  private connection: signalR.HubConnection | null = null;
  private retryTimeout: NodeJS.Timeout | null = null;
  private isManuallyStopped = false;
  private retryCount = 0;
  private readonly MAX_RETRY_ATTEMPTS = 10;
  private reloadTimeout: NodeJS.Timeout | null = null;

  private constructor() {}

  public static getInstance(): SignalRService {
    if (!SignalRService.instance) {
      SignalRService.instance = new SignalRService();
    }
    return SignalRService.instance;
  }

  public async startConnection(getToken: () => string | Promise<string>): Promise<void> {
    if (this.connection) return;

    this.isManuallyStopped = false;
    this.retryCount = 0;
    const hubUrl = `${BASE_API_URL}/adminNotificationHub`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => getToken(),
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onclose((error) => {
      console.log("SignalR Connection closed", error);
    });

    this.connection.onreconnecting((error) => {
      console.log("SignalR Connection reconnecting", error);
    });

    this.connection.onreconnected((connectionId) => {
      console.log("SignalR Connection reconnected. ConnectionId:", connectionId);
    });

    this.connection.on("ReceiveNotification", (success: boolean, message: string, data: any = null) => {
      if (success) {
        const isTargetPage = window.location.pathname.includes("/admin/outbound-message/fetch-message-template");
        if (isTargetPage) {
          toastEmitter.show(message + ' (Reloading in 2 second)', "success");
          // 1. Xóa timeout cũ nếu có thông báo mới đến dồn dập
          if (this.reloadTimeout) clearTimeout(this.reloadTimeout);
    
          // 2. Gán timeout mới
          this.reloadTimeout = setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          toastEmitter.show(message, "success");
        }
      } else {
        toastEmitter.show(message, "error");
      }
    });

    await this.startWithRetry();
  }

  private async startWithRetry(): Promise<void> {
    if (this.isManuallyStopped) return;

    if (this.retryCount >= this.MAX_RETRY_ATTEMPTS) {
      console.error(`SignalR: Maximum retry attempts (${this.MAX_RETRY_ATTEMPTS}) reached. Stopping connection attempts.`);
      await this.stopConnection();
      window.dispatchEvent(new CustomEvent("signalr_max_retries_reached"));
      return;
    }

    try {
      await this.connection?.start();
      console.log("SignalR Connected.");
      this.retryCount = 0; // Reset count on success
    } catch (err) {
      this.retryCount++;
      console.error(`SignalR Connection failed (Attempt ${this.retryCount}/${this.MAX_RETRY_ATTEMPTS}): `, err);
      // Manual retry every 5 seconds if initial start fails
      this.retryTimeout = setTimeout(() => this.startWithRetry(), 5000);
    }
  }

  public async stopConnection(): Promise<void> {
    this.isManuallyStopped = true;
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    if (this.connection) {
      try {
        if (this.reloadTimeout) {
          clearTimeout(this.reloadTimeout);
          this.reloadTimeout = null;
        }
        await this.connection.stop();
        console.log("SignalR Connection stopped.");
      } catch (err) {
        console.error("Error stopping SignalR connection:", err);
      } finally {
        this.connection = null;
      }
    }
  }

  public getConnection(): signalR.HubConnection | null {
    return this.connection;
  }
}

export default SignalRService;
